import React, { useEffect, useState } from 'react';
import { DiagnosticAssessmentResult } from '../../types/kAssessmentTypes';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePreKAudio } from '../../hooks/usePreKAudio';
import { CQPM_TARGETS_BY_TRACK, ACCURACY_PASS_THRESHOLD } from '../../types/constants';
import JourneyPath from '../KPractice/shared/JourneyPath';

interface KResultScreenProps {
  results: DiagnosticAssessmentResult;
  assessmentType: 'numbernaming' | 'subitizing' | 'comparing' | 'partwhole' | 'additionwithin5';
  onContinue: () => void;
}

// Shared action button component
const ActionButton: React.FC<{
  text: string;
  onClick: () => void;
  className?: string;
}> = ({ text, onClick, className = "" }) => (
  <motion.button
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.4 }}
    whileHover={{ scale: 1.08, rotate: 1 }}
    whileTap={{ scale: 0.92 }}
    onClick={onClick}
    className={`h-20 lg:h-24 px-16 rounded-3xl font-bold text-2xl lg:text-3xl shadow-xl transition-all duration-300 cursor-pointer hover:shadow-2xl transform-gpu ${className}`}
  >
    {text}
  </motion.button>
);

// Confetti particle component
const ConfettiParticle: React.FC<{ delay: number }> = ({ delay }) => {
  const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];
  const color = colors[Math.floor(Math.random() * colors.length)];

  return (
    <motion.div
      className="absolute w-2 h-2 rounded-full"
      style={{ backgroundColor: color }}
      initial={{
        x: Math.random() * window.innerWidth,
        y: -20,
        rotate: 0,
        opacity: 1
      }}
      animate={{
        y: window.innerHeight + 100,
        rotate: 360,
        opacity: 0
      }}
      transition={{
        duration: 2 + Math.random() * 1,
        delay: delay,
        ease: "easeOut"
      }}
    />
  );
};

// Helper to map assessment type to track ID for JourneyPath
const assessmentTypeToTrackId = (assessmentType: string): string => {
  switch (assessmentType) {
    case 'subitizing': return 'TRACK_K_SUBITIZING';
    case 'comparing': return 'TRACK_K_COMPARING';
    case 'partwhole': return 'TRACK_K_PARTWHOLE';
    case 'additionwithin5': return 'TRACK_K_ADDITION_WITHIN_5';
    default: return 'TRACK_K_NAMING';
  }
};

const KResultScreen: React.FC<KResultScreenProps> = ({ results, assessmentType, onContinue }) => {
  const navigate = useNavigate();
  const { playAudio } = usePreKAudio();
  const [showConfetti, setShowConfetti] = useState(false);

  const correctAnswers = results.correctAnswers || 0;
  const totalQuestions = results.totalQuestions || 0;
  const incorrectAnswers = totalQuestions - correctAnswers;
  const activeTimeElapsed = Math.max(Math.min(results.activeTimeElapsed || 0, 60000), 40000); // Clamp between 40s and 60s

  // Pass criteria by assessment type (using K-specific CQPM targets from constants)
  const getPassCriteria = (type: string) => {
    switch (type) {
      case 'numbernaming': return { minCQPM: CQPM_TARGETS_BY_TRACK.TRACK_K_NAMING };
      case 'subitizing': return { minCQPM: CQPM_TARGETS_BY_TRACK.TRACK_K_SUBITIZING };
      case 'comparing': return { minCQPM: CQPM_TARGETS_BY_TRACK.TRACK_K_COMPARING };
      case 'partwhole': return { minCQPM: CQPM_TARGETS_BY_TRACK.TRACK_K_PARTWHOLE };
      case 'additionwithin5': return { minCQPM: CQPM_TARGETS_BY_TRACK.TRACK_K_ADDITION_WITHIN_5 };
      default: return { minCQPM: CQPM_TARGETS_BY_TRACK.TRACK_K_NAMING }; // fallback
    }
  };

  const passCriteria = getPassCriteria(assessmentType);

  // Calculate CQPM based on active thinking time (excluding VOs and transitions)
  // Note: activeTimeElapsed is in milliseconds, need to convert to minutes
  const activeTimeMinutes = activeTimeElapsed > 0 ? activeTimeElapsed / 60000 : 1; // Convert ms to minutes, use 1 minute fallback if zero
  const cqpm = Math.floor(correctAnswers / activeTimeMinutes);
  const accuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

  // Check pass using CQPM and accuracy threshold
  const passed = cqpm >= passCriteria.minCQPM && accuracy >= ACCURACY_PASS_THRESHOLD;

  // Get assessment-specific display name
  const assessmentDisplayName = assessmentType === 'numbernaming' ? 'Number Naming' :
                                assessmentType === 'subitizing' ? 'Subitizing' :
                                assessmentType === 'comparing' ? 'Comparing' :
                                assessmentType === 'partwhole' ? 'Part Whole' : 'Addition Within 5';

  // Map assessment type to audio filename format
  const getAudioFilename = (prefix: string, assessmentType: string) => {
    const audioType = assessmentType === 'numbernaming' ? 'number_naming' : assessmentType;
    return `results/${prefix}_${audioType}.mp3`;
  };

  // Play audio and show confetti when pass screen loads, or play near miss audio
  useEffect(() => {
    if (passed) {
      // Play pass audio
      playAudio({ filename: getAudioFilename('result_pass', assessmentType) }).catch(() => {});

      // Show confetti for 600ms
      setShowConfetti(true);
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 600);

      return () => clearTimeout(confettiTimer);
    } else {
      // Play failed audio
      playAudio({ filename: getAudioFilename('result_fail', assessmentType) }).catch(() => {});
    }
  }, [passed, playAudio, assessmentType]);

  // Get track mapping for each K assessment type
  const getTrackMapping = (type: string) => {
    switch (type) {
      case 'numbernaming': return {
        trackId: 'TRACK_K_NAMING',
        practiceUrl: '/k-practice?track=naming', // TODO: Create K practice routes
        nextAssessment: 'subitizing'
      };
      case 'subitizing': return {
        trackId: 'TRACK_K_SUBITIZING',
        practiceUrl: '/k-practice?track=subitizing', // TODO: Create K practice routes
        nextAssessment: 'comparing'
      };
      case 'comparing': return {
        trackId: 'TRACK_K_COMPARING',
        practiceUrl: '/k-practice?track=comparing', // TODO: Create K practice routes
        nextAssessment: 'partwhole'
      };
      case 'partwhole': return {
        trackId: 'TRACK_K_PARTWHOLE',
        practiceUrl: '/k-practice?track=partwhole', // TODO: Create K practice routes
        nextAssessment: 'additionwithin5'
      };
      case 'additionwithin5': return {
        trackId: 'TRACK_K_ADDITION_WITHIN_5',
        practiceUrl: '/k-practice?track=additionwithin5', // TODO: Create K practice routes
        nextAssessment: null // Last K assessment
      };
      default: return {
        trackId: 'TRACK_K_NAMING',
        practiceUrl: '/k-practice?track=naming',
        nextAssessment: 'subitizing'
      };
    }
  };

  const handleContinue = () => {
    if (passed) {
      onContinue();
    } else {
      const mapping = getTrackMapping(assessmentType);
      navigate(mapping.practiceUrl);
    }
  };


  if (passed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col relative overflow-hidden">
        {/* Confetti Animation */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 50 }, (_, i) => (
              <ConfettiParticle key={i} delay={i * 0.02} />
            ))}
          </div>
        )}

        {/* Header Row - JourneyPath */}
        <div className="p-6">
          <JourneyPath currentTrack={assessmentTypeToTrackId(assessmentType)} />
        </div>

        {/* Main Content Area - Centered */}
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center space-y-8">
            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl lg:text-6xl font-bold text-gray-800"
            >
              Great Job!
            </motion.h1>

            {/* Subtitle */}
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl lg:text-4xl font-bold text-green-600 flex items-center justify-center gap-3"
            >
              <span className="text-4xl lg:text-5xl">✓</span>
              {assessmentDisplayName} Complete
            </motion.h2>

            {/* Metrics */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-lg lg:text-xl text-gray-600 font-medium space-y-2"
            >
              <div>Correct: {correctAnswers} • Active Time: {Math.round(activeTimeElapsed / 1000)}s</div>
              <div>CQPM: {cqpm} • Accuracy: {accuracy}%</div>
            </motion.div>

            {/* Continue Button */}
            <ActionButton
              text="Continue"
              onClick={handleContinue}
              className="bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700"
            />
          </div>
        </div>
      </div>
    );
  }

  // Failed case - consistent styling with pass
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row - JourneyPath */}
      <div className="p-6">
        <JourneyPath currentTrack={assessmentTypeToTrackId(assessmentType)} />
      </div>

      {/* Main Content Area - Centered */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center space-y-8">
          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl lg:text-6xl font-bold text-gray-800"
          >
            Nice try!
          </motion.h1>

          {/* Subtitle */}
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-3xl lg:text-4xl font-bold text-purple-600"
          >
            Let's practice it first!
          </motion.h2>

          {/* Metrics */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-lg lg:text-xl text-gray-600 font-medium space-y-2"
          >
            <div>Correct: {correctAnswers} • Active Time: {Math.round(activeTimeElapsed / 1000)}s</div>
            <div>CQPM: {cqpm} (need ≥{passCriteria.minCQPM}) • Accuracy: {accuracy}%</div>
          </motion.div>

          {/* Continue Button */}
          <ActionButton
            text="Continue"
            onClick={handleContinue}
            className="bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700"
          />
        </div>
      </div>
    </div>
  );
};

export default KResultScreen;